
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { MontonioPaymentMethod } from '../montonio/types';
import MontonioPaymentInterface from '@/components/checkout/MontonioPaymentInterface';
import { isValidMontonioPaymentMethod } from '../montonio/validators';

interface PaymentSectionProps {
  courseId: string | undefined;
}

const PaymentSection = ({ courseId }: PaymentSectionProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paymentError, setPaymentError] = React.useState<string | null>(null);
  const { email, phone, company, needsInvoice, finalAmount, loading, setField } = useCheckoutStore();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  
  const handleSubmitPayment = async (paymentInfo: {
    paymentMethod: MontonioPaymentMethod;
    selectedBank: string | null;
  }) => {
    console.log("💫 PaymentSection: Makse esitamine käivitatud infoga:", paymentInfo);
    
    if (!user || !courseId) {
      console.error("PaymentSection: Puudub kasutaja või kursuseID");
      toast({
        title: "Viga",
        description: "Makseks vajalikud andmed puuduvad",
        variant: "destructive",
      });
      return;
    }

    // Validate payment method
    if (!isValidMontonioPaymentMethod(paymentInfo.paymentMethod)) {
      console.error("PaymentSection: Kehtetu maksemeetod:", paymentInfo.paymentMethod);
      toast({
        title: "Makse viga",
        description: "Kehtetu maksemeetod valitud",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setPaymentError(null);
      setField('loading', { ...loading, payment: true });
      
      const paymentData = {
        courseId,
        userId: user.id,
        amount: finalAmount,
        currency: "EUR",
        email,
        phone,
        company: needsInvoice ? company : null,
        paymentMethod: paymentInfo.paymentMethod,
        selectedBank: paymentInfo.selectedBank
      };
      
      console.log("💫 PaymentSection: Makse algatamine andmetega:", paymentData);
      
      const { data, error } = await supabase.functions.invoke("montonio-payment", {
        body: paymentData
      });
      
      // Log the complete response data for debugging
      console.log("💫 PaymentSection: Complete response data:", data);
      
      if (error) {
        console.error("💫 PaymentSection: Makse algatamise viga:", error);
        throw new Error(error.message || "Makse algatamine ebaõnnestus");
      }
      
      // FIXED: Use the correct property name (camelCase paymentUrl instead of snake_case payment_url)
      if (!data?.paymentUrl) {
        console.error("💫 PaymentSection: Vastuses puudub makse URL:", data);
        throw new Error("Makse URL puudub vastuses");
      }
      
      console.log("💫 PaymentSection: Saadud makse URL:", data.paymentUrl);
      
      setIsRedirecting(true);
      toast({
        title: "Suunan maksekeskkonda",
        description: "Teid suunatakse makseportaali"
      });
      
      // Use window.location.assign for consistent redirection
      window.location.assign(data.paymentUrl);
      
    } catch (err: any) {
      console.error("💫 PaymentSection: Makse viga:", err);
      let errorMessage = err.message || "Makse ebaõnnestus – palun proovige uuesti.";
      
      if (errorMessage.includes('Montonio API')) {
        errorMessage = "Ühendus makseteenuse pakkujaga nurjus. Palun võta ühendust toega.";
      }
      
      setPaymentError(errorMessage);
      
      toast({
        title: "Makse viga",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setField('loading', { ...loading, payment: false });
    }
  };
  
  return (
    <Card id="payment" className="scroll-mt-24">
      <CardHeader className="pb-3">
        <CardTitle className="text-gray-700">4. Maksmine</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {paymentError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{paymentError}</AlertDescription>
          </Alert>
        )}
        
        <MontonioPaymentInterface 
          onSubmit={handleSubmitPayment}
          amount={String((finalAmount / 100).toFixed(2))}
          isTestMode={false}
          isRedirecting={isRedirecting}
        />
        
        <div className="flex items-center justify-center mt-6 text-sm text-gray-600">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-5 w-5 mr-2 text-green-600" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              fillRule="evenodd" 
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
              clipRule="evenodd" 
            />
          </svg>
          <span>Turvaline makse</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentSection;
